/* eslint-disable max-len */
import { Button, FormHelperText, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import HtmlQuillEditor from '../../../../../../components/Common/QuillEditor';
import ParticipantsSelector from '../../../../../../components/Common/ParticipantsSelector';
import { LanguageContext } from '../../../../../../libs/Context';
import { catchErr, isValidDate } from '../../../../../../libs/HelperContent';
import { createMeeting, OverlapMeeting, updateMeeting } from '../../../../../actions/meeting_actions';
import CustomSwitch from '../../../../../BusinessSettings/Components/BussinessCertification/components/CustomSwitch';
import ScheduleDatezone from '../../../../../Meetings/Components/Schedule/Components/ScheduleDatezone';
import SelectSeat from '../../../../../Meetings/Components/Schedule/Components/SelectSeat';
import TimeZone from '../../../../../Meetings/Components/Schedule/Components/TimeZone';
import AddAttachments from './AddAttachments';
import Password from './Password';
import axios from 'axios';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const CreateWebinar = (props) =>
{
	const { form, setForm, handleDeletePop, handleNextCallBack } = props;
	const { language } = useContext(LanguageContext);
	const [ data, setData ] = useState({ ...form });
	const [ error, setError ] = useState<any>({});
	const [ selectedTimezone, setSelectedTimezone ] = useState(data?.timeZone ?? 'Asia/Kolkata');
	const timeZoneRef = useRef<string>(data?.timeZone);
	const participantsRef = useRef<any>(null);
	const participantCount = useRef<any>([]); // used only for counting total participants and relations added
	const userData = useSelector((state:any) => state.mysession.session);
	const [ seatsOccupied, setSeatsOccupied ] = useState(0);
	const webinarManagement = userData?.companyData?.webinarManagement;
	let seatLimit = (webinarManagement?.seatCount || 0) - seatsOccupied;
	const [ openPassword, setOpenPassword ] = useState(data?.meetingPassword ?? false);

	seatLimit = seatLimit > 0 ? seatLimit : 0;

	// const handleMeetingName = (e) => // 18 code review changes done
	// {
	// 	const key = e.target.datase?.key || e.target.name;
	// 	const value = e.target.value.slice(0, 50);

	// 	setData((prev) => ({ ...prev, [key]: value }));
	// };

	const handleSet = (key, value) =>
	{
		setData((prev) => ({ ...prev, [key]: value }));
	};

	const handleError = (type, value?) =>
	{
		let formError:any = { };

		if (!value && typeof type === 'object') // when type is an object
		{
			formError = { ...type };
		}
		else if (type === 'participantLimit')
		{
			formError[type] = value;
		}

		setError({ ...error, formError });
	};

	const validateDescription = (meetingDescription) =>
	{
		// Remove leading <p><br></p> tags
		let trimmedDesc = meetingDescription.replace(/^(<p>\s*<br>\s*<\/p>\s*)+/, '');

		trimmedDesc = trimmedDesc.replace(/^(<blockquote>\s*<br>\s*<\/blockquote>\s*)+/, '');

		// Remove trailing <p><br></p> tags
		trimmedDesc = trimmedDesc.replace(/(<p>\s*<br>\s*<\/p>\s*)+$/, '');
		trimmedDesc = trimmedDesc.replace(/(<blockquote>\s*<br>\s*<\/blockquote>\s*)+$/, '');

		const parser = new DOMParser();

		const doc = parser.parseFromString(trimmedDesc, 'text/html');

		const ps = doc.querySelectorAll('p');

		ps.forEach((p) =>
		{
			// below code for removing all new empty lines from the meetingDescription
			// if (p.querySelector('br'))
			// {
			// 	if (p.parentNode)
			// 	{
			// 		p.parentNode.removeChild(p);
			// 	}
			// 	return;
			// }

			// Regular expression to match URLs
			const urlRegEx = /https?:\/\/[^\s/$.?#].[^\s]*/g;

			p.innerHTML = p.innerHTML.replace(urlRegEx, (url) =>
			{
				const anchorTag = document.createElement('a');

				anchorTag.href = url.replace(/"/g, '');
				anchorTag.textContent = url.replace(/"/g, '');
				anchorTag.target = '_blank';
				const anchorTagHTML = anchorTag.outerHTML;

				return anchorTagHTML;
			});
		});
		const htmlString = doc.documentElement.innerHTML;

		return htmlString;
	};

	const handleValidate = () =>
	{
		const errorForm: Record<string, string> = {};

		// Validate Meeting Name
		if (!data?.meetingName?.trim())
		{
			errorForm.meetingName = language?.pleaseEnterWebinarName;
		}

		// Validate Participant Limit
		if (!data?.participantLimit || data?.participantLimit < 2)
		{
			errorForm.participantLimit =
				data?.participantLimit === 0
					? language?.pleaseEnterSeat
					: language?.pleaseSelectMinTwoSeats;
		}
		else if (data?.participantLimit > seatLimit)
		{
			errorForm.participantLimit = language?.seatsCannotExceedTotalSeats;
		}

		// Validate Start Time
		const currentTime = dayjs().tz(selectedTimezone)
			.valueOf();
		const startTime = data?.startTimeUTC;

		if (!isValidDate(new Date(startTime)) || startTime <= currentTime)
		{
			errorForm.startTimeUTC = language?.pleaseEnterValidDateTime;
		}

		// Validate Duration
		if (!data?.duration || data?.duration <= 0)
		{
			errorForm.duration = language?.pleaseEnterWebinarDuration;
		}

		// Validate Password
		if (openPassword && !data?.meetingPassword?.trim())
		{
			errorForm.password = language?.pleaseEnterWebinarPassword;
		}
		else if (openPassword && data?.meetingPassword)
		{
			if (data?.meetingPassword.length < 4)
			{
				errorForm.password = 'Password must be at least 4 characters';
			}
			else if (data?.meetingPassword.length > 20)
			{
				errorForm.password = 'Password cannot exceed 20 characters';
			}
		}

		// Validate Time Zone
		if (!selectedTimezone?.trim())
		{
			errorForm.timeZone = language?.pleaseSelectTimezone;
		}

		// Validate Participants
		const participantsData = participantsRef?.current || {};
		const selectedParticipants = participantsData?.selectedParticipants || [];

		// Use selectedParticipants.length to avoid double counting
		// (selectedParticipants already contains all participants from both relation groups and individual selections)
		const totalParticipants = selectedParticipants.length;

		if (totalParticipants === 0)
		{
			errorForm.participants = language?.pleaseAddParticipants;
		}
		else if (totalParticipants > data?.participantLimit)
		{
			errorForm.participants = language?.participantsExceededTotalSeats;
		}

		return errorForm;
	};
	const handleSave = async () =>
	{
		const errorObj = handleValidate();

		setError(errorObj);
		if (Object.keys(errorObj).length > 0)
		{
			const elem = document.getElementById('formcontainer');

			elem?.scrollIntoView({ block: 'end' });

			return;
		}

		const meetingId = form?.id || data?.id;
		const defaultData = {
			withRegistration   : false,
			registrationFields : form?.registrationFields.slice(0, 2),
			moderation         : 1,
			// bannerImage        : [ { key: 'public/webinar/defaultbanner/banner1.jpg', name: 'banner1', size: 0, mediaType: 'image', type: 'image/jpg' } ],
			bannerImage        : form?.bannerImage,
			webinarTheme       : form?.webinarTheme
		};

		const body:any = {};

		body.meetingDetails =
		{
			meetingName      : data?.meetingName,
			timeZone         : selectedTimezone,
			timezoneObj      : {},
			startTimeUTC     : data?.startTimeUTC,
			participantLimit : data?.participantLimit,
			// scheduleType     : data?.scheduleType,
			// duration         : durationRef.current,
			duration         : data?.duration,
			attachments      : data?.attachments,
			...(meetingId ? {} : defaultData)
		};

		// Build participants data from ParticipantsSelector ref
		const participantsData = participantsRef?.current || {};
		const selectedRelations = participantsData?.relations || [];
		const selectedEmails = participantsData?.emails || [];
		const selectedParticipants = participantsData?.selectedParticipants || [];
		const allRelations = participantsData?.allRelations || [];
		const selectedDepartment = participantsData?.selectedDepartment || '';
		const departmentsList = participantsData?.departmentsList || [];
		const selectAllUsedForRelations = participantsData?.selectAllUsedForRelations || {};

		// Helper function to determine type based on relation name
		const getRelationType = (relationName: string) =>
		{
			const nameLower = relationName.toLowerCase();

			if (nameLower.includes('internal') || nameLower === 'company internal employee')
			{
				return 'self-company-group';
			}

			return 'relation-group';
		};

		// Helper function to determine individual participant type
		const getIndividualParticipantType = (relationName: string, participant: any, relationId: string) =>
		{
			const nameLower = relationName.toLowerCase();

			if (nameLower.includes('internal') || nameLower === 'company internal employee')
			{
				if (participant?.type === 'self-company-department' ||
					participant?.relationType === 'self-company-department' ||
					participant?.relationId?.includes('department') ||
					relationId?.includes('department'))
				{
					return 'self-company-department';
				}

				return 'self-company-employee';
			}

			return 'relation-company';
		};

		// Helper function to get department name
		const getDepartmentName = (participant: any) =>
		{
			if (participant?.type === 'self-company-department' || participant?.relationType === 'self-company-department')
			{
				return participant?.name || participant?.department || participant?.departmentName || '';
			}

			return participant?.department || participant?.departmentName || participant?.name || '';
		};

		// Build relations metadata array
		const relationsMetaArray: any = [];

		// Separate external emails from relation emails
		const externalEmails: string[] = [];
		const relationEmails: string[] = [];

		selectedEmails.forEach((email: string) =>
		{
			const participant = selectedParticipants.find((p: any) => (p?.email || p?.userId) === email);

			if (participant?.relationName?.toLowerCase() === 'others' || !participant?.relationId)
			{
				externalEmails.push(email);
			}
			else
			{
				relationEmails.push(email);
			}
		});

		// Get selected relation IDs
		const selectedRelationIds = selectedRelations.map((r: any) => r.relationId).filter(Boolean);

		// Add full relation groups
		if (selectedRelations?.length > 0)
		{
			selectedRelations.forEach((item: any) =>
			{
				const relationType = item?.type || item?.relationType || getRelationType(item?.relationName || '');

				relationsMetaArray.push({
					name   : item?.relationName,
					userId : item?.relationId,
					type   : relationType
				});
			});
		}

		// Group individual participants by relationId AND department
		const participantsByRelation: Record<string, any[]> = {};
		const participantsByDepartment: Record<string, any[]> = {};

		selectedParticipants.forEach((participant: any) =>
		{
			const relationId = participant?.relationId || participant?.userId;
			const relationName = participant?.relationName || '';
			const isInternalEmployee = relationName.toLowerCase().includes('internal') ||
				relationName.toLowerCase() === 'company internal employee';

			if (relationId && participant?.relationName?.toLowerCase() !== 'others')
			{
				// Skip if this participant belongs to a selected relation category
				if (selectedRelationIds.includes(relationId))
				{
					return;
				}

				// For Internal Employees, group by department if department is present
				if (isInternalEmployee && participant?.department)
				{
					const deptName = participant.department;

					if (!participantsByDepartment[deptName])
					{
						participantsByDepartment[deptName] = [];
					}

					participantsByDepartment[deptName].push(participant);
				}
				else
				{
					// Group by relation for non-internal or employees without department
					if (!participantsByRelation[relationId])
					{
						participantsByRelation[relationId] = [];
					}

					participantsByRelation[relationId].push(participant);
				}
			}
		});

		// Check if ALL internal employees are selected
		const internalEmployeesRelation = allRelations.find((r: any) =>
			r.relationName.toLowerCase().includes('internal') ||
			r.relationName.toLowerCase() === 'company internal employee'
		);

		const isInternalEmployeesRelationSelected = selectedRelationIds.includes(internalEmployeesRelation?.relationId);

		if (!isInternalEmployeesRelationSelected)
		{
			// Count total selected internal employees
			const totalSelectedInternalEmployeesFromDepts = Object.values(participantsByDepartment)
				.reduce((total: number, deptParticipants: any[]) => total + deptParticipants.length, 0);

			const internalEmployeesInRelation = participantsByRelation[internalEmployeesRelation?.relationId]?.length || 0;
			const totalSelectedInternalEmployees = totalSelectedInternalEmployeesFromDepts + internalEmployeesInRelation;
			const totalInternalEmployees = internalEmployeesRelation?.count || 0;

			const internalEmployeesInSelection = selectedParticipants.filter((p: any) =>
			{
				const relationName = p?.relationName || '';

				return relationName.toLowerCase().includes('internal') ||
					relationName.toLowerCase() === 'company internal employee';
			}).length;

			const hasInternalEmployees = internalEmployeesInSelection > 0;
			const noDepartmentFilter = !selectedDepartment || selectedDepartment === '' || selectedDepartment === 'All';
			const allCountMatches = totalInternalEmployees > 0 && totalSelectedInternalEmployees === totalInternalEmployees;
			const selectAllUsedForInternalEmployees = selectAllUsedForRelations[internalEmployeesRelation?.relationId] === true;
			const shouldTreatAsFullGroup = allCountMatches || (selectAllUsedForInternalEmployees && hasInternalEmployees && noDepartmentFilter);

			if (shouldTreatAsFullGroup)
			{
				relationsMetaArray.push({
					name   : internalEmployeesRelation?.relationName || 'Company Internal Employee',
					userId : internalEmployeesRelation?.relationId,
					type   : 'self-company-group'
				});
			}
			else
			{
				// Process department groups
				Object.keys(participantsByDepartment).forEach((deptName) =>
				{
					const participants = participantsByDepartment[deptName];
					const firstParticipant = participants[0];
					const relationId = firstParticipant?.relationId || firstParticipant?.userId;

					const department = departmentsList.find((d: any) =>
					{
						const deptNameFromList = d.name || d.departmentName || d;

						return deptNameFromList === deptName;
					});

					const totalDeptEmployees = department?.employeeCount || department?.count || department?.employees?.length || department?.totalEmployees || 0;
					const selectedCount = participants.length;
					const allEmployeesSelected = totalDeptEmployees > 0 && selectedCount === totalDeptEmployees;

					if (allEmployeesSelected)
					{
						const deptId = department?._id || department?.id || `dept-${deptName}`;

						relationsMetaArray.push({
							name   : deptName,
							userId : deptId,
							type   : 'self-company-department'
						});
					}
					else
					{
						// Add individual employees
						participants.forEach((participant: any) =>
						{
							const participantUserId = participant?.userId || '';

							relationsMetaArray.push({
								name   : participant?.name || deptName,
								userId : participantUserId || relationId,
								type   : 'self-company-employee'
							});
						});
					}
				});
			}
		}

		// Process grouped participants by relation
		const internalEmployeesAlreadyAddedAsGroup = relationsMetaArray.some((item: any) =>
			item.type === 'self-company-group' &&
			(item.name.toLowerCase().includes('internal') || item.name.toLowerCase() === 'company internal employee')
		);

		Object.keys(participantsByRelation).forEach((relationId) =>
		{
			const participants = participantsByRelation[relationId];
			const firstParticipant = participants[0];
			const relationName = firstParticipant?.relationName || '';

			const isInternalEmployee = relationName.toLowerCase().includes('internal') ||
				relationName.toLowerCase() === 'company internal employee';

			if (isInternalEmployee && internalEmployeesAlreadyAddedAsGroup)
			{
				return;
			}

			const relation = allRelations.find((r: any) => r.relationId === relationId);
			const selectedCount = participants.length;
			const totalCount = relation?.count || 0;
			const allParticipantsSelected = totalCount > 0 && selectedCount === totalCount;
			const selectAllUsedForThisRelation = selectAllUsedForRelations[relationId] === true;

			if (allParticipantsSelected || selectAllUsedForThisRelation)
			{
				const relationType = relation?.type || relation?.relationType || getRelationType(relationName);

				relationsMetaArray.push({
					name   : relationName,
					userId : relationId,
					type   : relationType
				});
			}
			else
			{
				// Add individual participants
				participants.forEach((participant: any) =>
				{
					const participantUserId = participant?.userId || '';
					const participantType = participant?.type || participant?.relationType ||
						getIndividualParticipantType(relationName, participant, relationId);

					let displayName = participant?.name || relationName;

					if (participantType === 'self-company-department')
					{
						displayName = getDepartmentName(participant) || relationName;
					}
					else if (participantType === 'self-company-employee')
					{
						displayName = participant?.name || relationName;
					}
					else if (participantType === 'relation-company')
					{
						displayName = participant?.name || relationName;
					}

					const metaItem: any = {
						name   : displayName,
						userId : participantUserId || relationId,
						type   : participantType
					};

					// Add relationId for individual companies from relations
					if (participantType === 'relation-company' && relationId)
					{
						metaItem.relationId = relationId;
						metaItem.relation = relationName;
					}

					relationsMetaArray.push(metaItem);
				});
			}
		});

		// Build shareTo array
		const relationUserIds = relationsMetaArray.map((item: any) => item.userId).filter(Boolean);
		const allShareTo = [ ...relationUserIds, ...externalEmails ];

		body.shareTo = allShareTo;
		body.metaData = {
			relations       : relationsMetaArray,
			externalMembers : externalEmails
		};

		body.scheduleType = 'webinar';

		body.meetingDetails.endTimeUTC = data.startTimeUTC + data?.duration * 60 * 1000;

		if (data?.onlyInvities) body.meetingDetails.onlyInvities = data?.onlyInvities;
		if (data?.recording) body.meetingDetails.recording = data?.recording;
		// eslint-disable-next-line max-len
		// if (data?.password?.isPassword) body.meetingDetails.meetingPassword = data?.password?.meetingPassword;
		body.meetingDetails.meetingPassword = data?.meetingPassword;

		const trimmedDesc = validateDescription(data?.meetingDescription);

		if (![ '', '<p><br></p>' ].includes(trimmedDesc)) body.meetingDetails.meetingDescription = trimmedDesc;

		const newForm:any = {
			...data,
			shareTo            : body.shareTo,
			metaData           : body.metaData,
			meetingDescription : trimmedDesc,
			timeZone           : selectedTimezone,
			endTimeUTC        	: body.meetingDetails.endTimeUTC
		};

		setData((prevData) => ({
			...prevData,
			...newForm
		}));

		try
		{
			let res;

			if (meetingId)
			{
				body.actionType = 'updateWebinar';
				body.id = meetingId;
				res = await updateMeeting(body);

				await axios
					.post(`${process.env.REACT_APP_SERVER_CONFIG}updateMeet`, {
						sessionId : form?.id,
						meetInfo  : { ...body?.meetingDetails }
					});
			}
			else
			{
				res = await createMeeting(body);

				newForm.id = res?.response?._id;
			}

			if (res?.status)
			{
				setForm(newForm);
				handleNextCallBack();
			}
			else
			{
				setForm({});
			}
		}
		catch (err)
		{
			catchErr(err);
		}
	};

	const CheckOverlap = async () =>
	{
		try
		{
			const body:any = {
				comId        : userData?.companyId,
				startTime    : data?.startTimeUTC,
				endTime      : data?.startTimeUTC + Number(data?.duration || 0) * 60000,
				scheduleType : 'webinar'
			};

			if (!body.startTime || !body.endTime) return;
			const res = await OverlapMeeting(body);

			if (res?.status)
			{
				const occupiedSeats = res?.data.reduce((acc, item) =>
					(acc + (data?.id && (item?._id === data?.id) ? 0 : item?.participantLimit))
				, 0);

				setSeatsOccupied(occupiedSeats || 0);
			}
		}
		catch (err)
		{
			catchErr(err);
		}
	};

	useEffect(() =>
	{
		CheckOverlap();
	}, [ data?.duration, data?.startTimeUTC ]);

	// Update date when timezone changes (only if no existing date from data)
	useEffect(() =>
	{
		if (data?.startTimeUTC)
		{
			return;
		}
		const nowInTimezone = dayjs().tz(selectedTimezone)
			.add(5, 'minutes');
		const utcDate = nowInTimezone.utc();

		setData((prev) => ({ ...prev, startTimeUTC: utcDate.valueOf() }));
	}, [ selectedTimezone ]);

	useEffect(() =>
	{
		setOpenPassword(Boolean(data?.meetingPassword));
	}, [ form?.meetingPassword ]);

	const handleCancel = () =>
	{
		handleDeletePop(data);
	};

	return (
		<>
			<Stack direction='column' height='calc(100% - 56px)'>

				<Grid container px='var(--pixel16)' py='6px'>
					<span id='formcontainer'/>
					<Grid item xs={12}>
						<Stack spacing={1}>
							<InputLabel style={{ fontSize: 'var(--pixel14)', color: 'var(--black)' }}>{language?.webinarName}*</InputLabel>
							<TextField inputProps={{ maxLength: 50 }} value={data?.meetingName} onChange={(e) => handleSet('meetingName', e.target.value)} fullWidth placeholder={language?.enterWebinarName} size='small' />
						</Stack>
						{error?.meetingName && (
							<FormHelperText id='txtEmail-error' className='errorTxt'>
								{error?.meetingName}
							</FormHelperText>
						)}
						<Typography textAlign={'end'} variant='body2'>{data?.meetingName?.length}/50</Typography>
						<Stack mt={1}>
							<InputLabel style={{ fontSize: 'var(--pixel14)', color: 'var(--black)', paddingBottom: 'var(--pixel8)' }}>{language?.webinarDescript}</InputLabel>
							<HtmlQuillEditor text={data?.meetingDescription || ''} onChange={(newDesc) => handleSet('meetingDescription', newDesc)} maxLength={1000}/>
						</Stack>
						<Grid container spacing={1.5} mt={1}>
							{/* time zone selection */}
							<TimeZone
								ref={timeZoneRef}
								formDate={data?.startTimeUTC}
								handleUpdateDate={(dt) => handleSet('startTimeUTC', dt.valueOf())}
								dataTimeZone={timeZoneRef?.current}
								onTimezoneChange={setSelectedTimezone}
							/>
							<SelectSeat
								number={data?.participantLimit}
								seatLimit={seatLimit}
								seatsOccupied={seatsOccupied}
								handleSet={handleSet}
								handleError={handleError}
								error={error}
							/>
						</Grid>
						{/* date time and duration selection */}
						<ScheduleDatezone
							formDate={data?.startTimeUTC}
							handleUpdateDate={(dt) => handleSet('startTimeUTC', dt.valueOf())}
							defaultDuration={data?.duration}
							HandleDuration={(val) => handleSet('duration', val)}
							selectedTimezone={selectedTimezone}
						/>
						<div className='flexRow'>
							<div style={{ width: 'var(--percent50)' }}>
								{error?.startTimeUTC && (
									<FormHelperText id='txtEmail-error' className='errorTxt'>
										{error?.startTimeUTC}
									</FormHelperText>
								)}
							</div>
							<div style={{ width: 'var(--percent50)' }}>
								{error?.duration && (
									<FormHelperText id='txtEmail-error' className='errorTxt'>
										{error?.duration}
									</FormHelperText>
								)}
							</div>
						</div>
						<Grid container py='var(--pixel12)' >
							<ParticipantsSelector
								ref={participantsRef}
								setFormError={setError}
								formError={error}
								noOfSeats={data?.participantLimit}
								participantCount={participantCount}
								shareTo={data?.metaData ?? {}}
								participants={form?.metaData?.externalMembers ?? []}
							/>
						</Grid>
						{error?.participants && (
							<FormHelperText id='txtEmail-error' className='errorTxt'>
								{error?.participants}
							</FormHelperText>
						)}
						<Stack direction={'row'} display={'flex'} alignItems={'center'} py={'var(--pixel4)'} mt={1.5} width={'var(--percent100)'}>
							{/* password field */}
							<Password openPassword={openPassword} setOpenPassword={setOpenPassword} data={data} handleSet={handleSet}/>
						</Stack>
						{error?.password && (
							<FormHelperText id='txtEmail-error' className='errorTxt'>
								{error?.password}
							</FormHelperText>
						)}
						<Stack direction={'row'} display={'flex'} alignItems={'center'} py={'var(--pixel4)'}>
							<CustomSwitch
								toggleEnabled={Boolean(data?.recording)}
								// handleToggleChange={(e, checked) => handleSwitch(e, checked, 'recording')}
								handleToggleChange={(e, checked) => handleSet('recording', checked)}
							/>
							<Typography>{language?.cloudRecording}</Typography>
						</Stack>
						<Stack direction={'row'} display={'flex'} alignItems={'center'} py={'var(--pixel4)'}>
							<CustomSwitch
								toggleEnabled={Boolean(data?.onlyInvities)}
								handleToggleChange={(e, checked) => handleSet('onlyInvities', checked)}
							/>
							<Typography variant='inherit' display={'flex'} flexDirection={'row'}>{language?.ByInviteOnly} <Typography variant='inherit' color='#959595' pl='var(--pixel4)'>({language?.webirAccsOnly})</Typography></Typography>
						</Stack>
						<AddAttachments attachments={data?.attachments} handleSet={handleSet}/>
					</Grid>
				</Grid>
			</Stack>
			<Grid
				container
				justifyContent={'flex-end '}
				alignItems='center'
				p='var(--pixel16)'
			>
				<Grid item>
					<Stack direction='row' spacing={1}>
						<Button
							onClick={handleCancel}
							variant='outlined'
						>
							{language?.cancel}
						</Button>
						<Button
							variant='contained'
							sx={{ color: 'var(--whiteColor)' }}
							onClick={handleSave}
						>
							{language?.next}
						</Button>
					</Stack>
				</Grid>
			</Grid>
		</>
	);
};

export default CreateWebinar;
